import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, SIZES } from "@/lib/api";
import { useData } from "@/lib/data";
import { detectKoli, groupByProduct } from "@/lib/koli";
import { Printer } from "lucide-react";
import { Logo } from "@/components/Logo";

const cell = "border border-black px-2 py-1.5";

export default function OrderPrint() {
  const { id } = useParams();
  const { customers, productById, koliTypes } = useData();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="p-10 text-[#EF4444]" data-testid="order-print-not-found">Sipariş bulunamadı.</div>;
  if (!order) return <div className="p-10 text-[#6B7280]">Yükleniyor...</div>;

  const customer = customers.find((c) => c.id === order.customer_id);
  const rows = Object.entries(groupByProduct(order.items)).map(([pid, sizes]) => {
    const k = detectKoli(sizes, koliTypes);
    return { pid, sizes, p: productById(pid), total: Object.values(sizes).reduce((a, b) => a + b, 0),
      koli: k.koli_count ? `${k.koli_count} × ${koliTypes.find((t) => t.id === k.koli_type_id)?.name}` : "—" };
  });
  const usedSizes = SIZES.filter((s) => rows.some((r) => r.sizes[s]));
  const grand = rows.reduce((a, r) => a + r.total, 0);
  const totalKoli = rows.reduce((a, r) => a + (parseInt(r.koli) || 0), 0);
  const sizeTotal = (s) => rows.reduce((a, r) => a + (r.sizes[s] || 0), 0);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="order-print-page">
      <div className="max-w-[900px] mx-auto p-8 sm:p-12">
        <div className="flex justify-end mb-6 print:hidden">
          <button onClick={() => window.print()} data-testid="order-print-btn"
            className="flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white px-5 h-11 rounded-md font-display uppercase tracking-wide">
            <Printer className="w-4 h-4" /> Yazdır
          </button>
        </div>
        <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
          <div>
            <Logo dark className="text-4xl" subtitle="Ayakkabı Üretim" testId="print-logo" />
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-4" data-testid="print-title">Sipariş Onay Formu</h1>
          </div>
          <div className="text-right text-sm font-mono">
            <p>No: <b data-testid="order-print-no">{order.id.slice(0, 8).toUpperCase()}</b></p>
            <p>Sipariş Tarihi: {new Date(order.created_at).toLocaleDateString("tr-TR")}</p>
            <p>Teslim Tarihi: <b data-testid="order-print-delivery">{new Date(order.delivery_date).toLocaleDateString("tr-TR")}</b></p>
          </div>
        </div>
        <div className="mb-6 text-sm">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Müşteri</p>
          <p className="text-lg font-semibold" data-testid="order-print-customer">{order.customer_name}</p>
          {customer?.contact_name && <p>Yetkili: {customer.contact_name}</p>}
          {customer?.phone && <p>Tel: {customer.phone}</p>}
          {customer?.address && <p>{customer.address}</p>}
        </div>
        <table className="w-full text-sm border border-black" data-testid="order-print-table">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className={cell}>Model</th>
              <th className={cell}>Renk</th>
              {usedSizes.map((s) => <th key={s} className={`${cell} text-center`}>{s}</th>)}
              <th className={`${cell} text-right`}>Çift</th>
              <th className={`${cell} text-right`}>Koli</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.pid} data-testid={`order-print-row-${r.pid}`}>
                <td className={`${cell} font-mono`}>{r.p?.model_code || "—"}</td>
                <td className={cell}>{r.p?.model_color || "—"}</td>
                {usedSizes.map((s) => <td key={s} className={`${cell} text-center font-mono`}>{r.sizes[s] || ""}</td>)}
                <td className={`${cell} text-right font-mono font-bold`}>{r.total}</td>
                <td className={`${cell} text-right font-mono whitespace-nowrap`}>{r.koli}</td>
              </tr>
            ))}
            <tr className="font-bold">
              <td colSpan={2} className={`${cell} text-right`}>TOPLAM</td>
              {usedSizes.map((s) => <td key={s} className={`${cell} text-center font-mono`}>{sizeTotal(s)}</td>)}
              <td className={`${cell} text-right font-mono`} data-testid="order-print-total">{grand}</td>
              <td className={`${cell} text-right font-mono`} data-testid="order-print-koli-total">{totalKoli || "—"}</td>
            </tr>
          </tbody>
        </table>
        {koliTypes.length > 0 && (
          <p className="text-xs text-gray-600 mt-3 font-mono">
            {koliTypes.map((t) => `${t.name}: ${Object.entries(t.ratios).map(([s, q]) => `${s}×${q}`).join(" · ")}`).join("  |  ")}
          </p>
        )}
        {order.note && <p className="text-sm mt-4">Not: {order.note}</p>}
        <div className="grid grid-cols-2 gap-10 mt-16 text-sm text-center">
          <div className="border-t border-black pt-2">CARRANO (Hazırlayan)</div>
          <div className="border-t border-black pt-2">Müşteri Onayı</div>
        </div>
        {customer?.cargo_company && (
          <p className="text-sm mt-10" data-testid="order-print-cargo">Not: Kargo firması <b>{customer.cargo_company}</b></p>
        )}
      </div>
    </div>
  );
}
